import { Toaster } from "../../ui/toaster";
import { Toaster as Sonner } from "../../ui/sonner"
import { TooltipProvider } from "../../ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppLayout } from "../../components/UnderwritingEngineV2/AppLayout";
import LoanCalculate from "./pages/LoanCalculate";
import Controller from "./pages/Controller";
import ManageAccess from "./pages/ManageAccess";
import "../../assets/css/UnderwritingEngineV2/style.css";
import { Routes, Route, Navigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { mapUrlToProductId } from "../../utils/common/mapUrlToProduct";
import { productRoleName } from "../../utils/common/productRoles";
import UserService from "../../services/UserService";
import serverConfig from "../../config/server";
import axios from "axios";
import { Link } from "react-router-dom";

const UNDERWRITING_ENGINE_PRODUCT_ID = mapUrlToProductId("underwriting-engine") as number;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  const [currentStep, setCurrentStep] = useState<"Loans" | "Application" | "Score" | "Review" | "Underwrite">("Loans");
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const productId = mapUrlToProductId("underwriting-engine");
    UserService.ensureFreshToken()
      .then((jwt: string) =>
        axios.post(`${serverConfig.api_base_url}check-product-access`, {
          jwt,
          product_id: productId,
        })
      )
      .then((res: any) => {
        setHasAccess(res.data.has_access === true);
      })
      .catch(() => {
        setHasAccess(false);
      })
      .finally(() => {
        setAccessChecked(true);
      });
  }, []);

  // Important: shadcn/Radix components render via portals into <body>.
  // If we scope theme tokens only to an inner wrapper, portal content won't inherit them.
  // So while UnderwritingEngineV2 is mounted, apply a root class + dark mode on <html>.
  React.useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("underwriting-engine-v2-root", "dark");
    body.style.backgroundColor = "hsl(218, 30%, 8%)";
    body.style.color = "hsl(30, 9%, 95%)";
    body.style.margin = "0";
    body.style.padding = "0";
    return () => {
      root.classList.remove("underwriting-engine-v2-root", "dark");
      body.style.backgroundColor = "";
      body.style.color = "";
      body.style.margin = "";
      body.style.padding = "";
    };
  }, []);

  // Only the index page is gated on the product subscription check below.
  // /controller and /manage-access are self-contained: each gates itself on its own
  // product-role check (Controller.tsx, ManageAccess.tsx), independent of `hasAccess` --
  // this must stay true so an admin-only user (denied `hasAccess` by design, see
  // has_any_product_role) can still reach /manage-access to grant a working tier.
  const isProductAdmin: boolean = UserService.hasRole([
    productRoleName(UNDERWRITING_ENGINE_PRODUCT_ID, "admin"),
  ]);

  let indexElement: React.ReactNode;
  if (!accessChecked) {
    indexElement = (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <p className="text-muted-foreground text-sm">Checking access…</p>
      </div>
    );
  } else if (!hasAccess) {
    indexElement = (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-3">
          <h2 className="text-lg font-semibold" style={{ color: "#FD9F29" }}>Access Denied</h2>
          <p className="text-sm text-muted-foreground">
            You don't have an active subscription to this product.
          </p>
          {isProductAdmin && (
            <Link to="/underwriting-engine/manage-access" className="inline-block text-sm text-amber hover:underline">
              Manage Access →
            </Link>
          )}
        </div>
      </div>
    );
  } else {
    indexElement = (
      <AppLayout currentStep={currentStep} onStepChange={setCurrentStep}>
        <LoanCalculate currentStep={currentStep} setCurrentStep={setCurrentStep} />
      </AppLayout>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <div
          className="underwriting-engine-v2-theme dark"
          style={{
            backgroundColor: 'hsl(218, 30%, 8%)',
            minHeight: '100vh',
            color: 'hsl(30, 9%, 95%)'
          }}
        >
          <Routes>
            <Route index element={indexElement} />
            <Route path="controller" element={<Controller />} />
            <Route path="manage-access" element={<ManageAccess />} />
            <Route path="*" element={<Navigate to="/underwriting-engine" replace />} />
          </Routes>
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;

